import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Avatar,
  Badge,
  Button,
  Card,
  DesignV2Scope,
  EmptyState,
  ListRow,
  Menu,
  Skeleton,
  StickyPageHeader,
} from "../design-v2";
import { ProgramSummaryCard } from "../components/product";
import SettingsDrawer from "../features/settings/SettingsDrawer";
import { getCycleDetailsPath } from "../features/multiWeek/routes";
import { isAIWeeklyPlanFrontendEnabled } from "../features/weeklyPlans/featureFlags";
import { formatRelativeCreatedLabel } from "../features/weeklyPlans/formatters";
import { mapWeeklyPlanListItemToUi } from "../features/weeklyPlans/mappers";
import { buildOrigin } from "../features/weeklyPlans/navigation";
import {
  getAIWeeklyPlanBuilderPath,
  getWeeklyPlanDetailsPath,
  getWeeklyPlansPath,
} from "../features/weeklyPlans/routes";
import {
  getProgramOverviewV2,
  getUserSettings,
  getWeeklyPlans,
} from "../services/api";
import "./program.css";

const LOAD_STATUS = Object.freeze({
  UNKNOWN: "unknown",
  RESOLVED: "resolved",
  ERROR: "error",
});

const PROGRAM_STATE = Object.freeze({
  UNKNOWN: "unknown",
  EMPTY: "empty",
  LOADED: "loaded",
  ERROR: "error",
});

const EMPTY_ARRAY = [];

function formatDisplayDate(dateKey) {
  if (!dateKey) {
    return "";
  }

  return new Date(`${dateKey}T00:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function getLocalDateKey(timeZone = "America/Toronto") {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const map = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${map.year}-${map.month}-${map.day}`;
}

function isActiveOnDate(item, todayDateKey) {
  if (!item?.startDate || !item?.endDate) {
    return false;
  }

  return item.startDate <= todayDateKey && item.endDate >= todayDateKey;
}

function getMondayWeekStart(dateKey) {
  if (!dateKey) {
    return null;
  }

  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const day = date.getDay();
  const daysFromMonday = day === 0 ? 6 : day - 1;
  date.setDate(date.getDate() - daysFromMonday);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const dayOfMonth = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${dayOfMonth}`;
}

function createNeutralTimelineSlots() {
  return Array.from({ length: 12 }).map((_, index) => ({
    slotIndex: index + 1,
    label: `W${index + 1}`,
    status: "neutral",
    weekNumber: null,
    startDate: null,
    endDate: null,
    isCurrent: false,
    isCompleted: false,
    isInActiveCycle: false,
    showNowLabel: false,
    showCheckmark: false,
    showNextCycleHint: false,
  }));
}

function PlusIcon() {
  return (
    <svg className="lz-program__icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function SparkleIcon() {
  return (
    <svg className="lz-program__menu-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="m10 2.5 1.9 5.2 5.2 1.9-5.2 1.9-1.9 5.2-1.9-5.2-5.2-1.9 5.2-1.9L10 2.5Z" />
      <path d="m18.2 14.5.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8.8-2.2Z" />
    </svg>
  );
}

function SlidersIcon() {
  return (
    <svg className="lz-program__menu-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 7h10M18 7h2M4 17h2M10 17h10M14 4v6M6 14v6" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg className="lz-program__row-icon" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3.5" y="5" width="17" height="15" rx="2" />
      <path d="M8 3v4M16 3v4M3.5 10h17" />
    </svg>
  );
}

function HistoryIcon() {
  return (
    <svg className="lz-program__row-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4.5 8.5V4m0 0H9M4.5 4A9 9 0 1 1 3 14" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function ProgramIcon() {
  return (
    <svg className="lz-program__empty-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 7v10M17 7v10M4 9v6M20 9v6M7 12h10" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg className="lz-program__chevron" viewBox="0 0 24 24" aria-hidden="true">
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

function BookmarkIcon({ filled }) {
  return (
    <svg
      className="lz-program__bookmark"
      viewBox="0 0 24 24"
      aria-hidden="true"
      data-filled={filled ? "true" : "false"}
    >
      <path d="M6 4.5A1.5 1.5 0 0 1 7.5 3h9A1.5 1.5 0 0 1 18 4.5V21l-6-3.5L6 21V4.5Z" />
    </svg>
  );
}

function formatPlanMetadataLabel(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "ai") {
    return "AI";
  }

  return normalized
    .split("_")
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(" ");
}

function WeeklyPlanMetadata({ source, status }) {
  const normalizedSource = String(source || "").trim().toLowerCase();
  const normalizedStatus = String(status || "").trim().toLowerCase();
  const sourceTone = normalizedSource === "ai"
    ? "ai"
    : normalizedSource === "manual"
      ? "manual"
      : "neutral";
  const statusTone = normalizedStatus === "published"
    ? "published"
    : normalizedStatus === "draft"
      ? "draft"
      : "neutral";

  return (
    <span className="lz-program__metadata-pills">
      <Badge
        className={`lz-program__metadata-pill lz-program__metadata-pill--source-${sourceTone}`}
        data-plan-source={normalizedSource}
      >
        {formatPlanMetadataLabel(source)}
      </Badge>
      <Badge
        className={`lz-program__metadata-pill lz-program__metadata-pill--status-${statusTone}`}
        data-plan-status={normalizedStatus}
      >
        {formatPlanMetadataLabel(status)}
      </Badge>
    </span>
  );
}

function ProgramLoading() {
  return (
    <div className="lz-program__loading" aria-busy="true" data-testid="program-loading">
      <Skeleton shape="block" height={260} aria-label="Loading program overview" />
      <section className="lz-program__section" aria-labelledby="loading-cycle-structure">
        <h2 id="loading-cycle-structure" className="lz-program__section-title">
          Cycle Structure
        </h2>
        <Skeleton
          shape="block"
          height={188}
          aria-label="Loading cycle structure"
          data-testid="program-loading-timeline"
        />
      </section>
      <div className="lz-program__loading-rows" aria-label="Loading program lists">
        {Array.from({ length: 3 }, (_, index) => (
          <Skeleton key={index} shape="block" height={76} />
        ))}
      </div>
    </div>
  );
}

function ProgramSectionError({ children }) {
  return (
    <Card className="lz-program__error" role="alert">
      {children}
    </Card>
  );
}

function getTimelineStatusLabel(slot, isUpcomingNextCycleStart) {
  if (slot.status === "current" || slot.isCurrent || slot.showNowLabel) {
    return "NOW";
  }
  if (slot.status === "deload") {
    return "REST";
  }
  if (slot.status === "next_cycle" || slot.showNextCycleHint || isUpcomingNextCycleStart) {
    return "NEW";
  }
  return null;
}

// This horizon is specific to the Program overview. Phase 5 uses dynamic
// builder WeekRail and WeeklyVolumeChart concepts, so a shared CycleTimeline
// would conflate different product semantics without genuine reuse.
function ProgramCycleTimeline({
  timelineRows,
  programMode,
  activeCycleRange,
  upcomingCycleRanges,
  firstUpcomingCycleRange,
  nextCycleRange,
}) {
  return (
    <Card
      className="lz-program__timeline"
      padding="lg"
      data-testid="program-cycle-timeline"
      data-layout="segmented-2x6"
    >
      {timelineRows.map((row, rowIndex) => (
        <div className="lz-program__timeline-row" key={rowIndex}>
          {row.map((slot) => {
            const isInActiveCycleRange =
              programMode === "active" &&
              Number(slot.slotIndex) >= Number(activeCycleRange?.startSlotIndex) &&
              Number(slot.slotIndex) <= Number(activeCycleRange?.endSlotIndex);
            const isInUpcomingCycleRange =
              (programMode === "upcoming_only" &&
                upcomingCycleRanges.some(
                  (range) =>
                    Number(slot.slotIndex) >= Number(range.startSlotIndex) &&
                    Number(slot.slotIndex) <= Number(range.endSlotIndex)
                )) ||
              (programMode === "active" &&
                Number(slot.slotIndex) >= Number(nextCycleRange?.startSlotIndex) &&
                Number(slot.slotIndex) <= Number(nextCycleRange?.endSlotIndex));
            const isUpcomingNextCycleStart =
              programMode === "upcoming_only" &&
              Number(slot.slotIndex) === Number(firstUpcomingCycleRange?.startSlotIndex);
            const range = isInActiveCycleRange
              ? "active"
              : isInUpcomingCycleRange
                ? "upcoming"
                : "none";
            const statusLabel = getTimelineStatusLabel(slot, isUpcomingNextCycleStart);

            return (
              <div
                key={slot.slotIndex}
                className={[
                  "lz-program__week",
                  `lz-program__week--${slot.status}`,
                  `lz-program__week--range-${range}`,
                  isUpcomingNextCycleStart && "lz-program__week--upcoming-start",
                ].filter(Boolean).join(" ")}
                data-program-week-slot={slot.slotIndex}
                data-status={slot.status}
                data-range={range}
              >
                <span className="lz-v2-visually-hidden">
                  {slot.label}: {slot.status.replaceAll("_", " ")}
                </span>
                <span className="lz-program__week-label-space">
                  {statusLabel ? (
                    <Badge
                      tone={statusLabel === "NEW" ? "info" : "action"}
                      className="lz-program__week-status"
                    >
                      {statusLabel}
                    </Badge>
                  ) : null}
                </span>
                <span className="lz-program__week-segment" aria-hidden="true" />
                <span className="lz-program__week-number">{slot.label}</span>
              </div>
            );
          })}
        </div>
      ))}
    </Card>
  );
}

export default function Program() {
  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [profileAvatar, setProfileAvatar] = useState({ url: null, sex: null });
  const [weeklyPlans, setWeeklyPlans] = useState([]);
  const [programOverview, setProgramOverview] = useState(null);
  const [weeklyPlansLoadStatus, setWeeklyPlansLoadStatus] = useState(LOAD_STATUS.UNKNOWN);
  const [programOverviewLoadStatus, setProgramOverviewLoadStatus] = useState(
    LOAD_STATUS.UNKNOWN
  );
  const settingsTriggerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isAIBuilderEnabled = isAIWeeklyPlanFrontendEnabled();

  useEffect(() => {
    let isMounted = true;

    async function loadData() {
      const [weeklyResult, overviewResult] = await Promise.allSettled([
        getWeeklyPlans(),
        getProgramOverviewV2(),
      ]);

      if (!isMounted) {
        return;
      }

      if (weeklyResult.status === "fulfilled") {
        setWeeklyPlans(
          (weeklyResult.value.items || []).map((item) =>
            mapWeeklyPlanListItemToUi(item, formatRelativeCreatedLabel(item.createdAt))
          )
        );
        setWeeklyPlansLoadStatus(LOAD_STATUS.RESOLVED);
      } else {
        setWeeklyPlansLoadStatus(LOAD_STATUS.ERROR);
      }

      if (overviewResult.status === "fulfilled") {
        setProgramOverview(overviewResult.value);
        setProgramOverviewLoadStatus(LOAD_STATUS.RESOLVED);
      } else {
        setProgramOverviewLoadStatus(LOAD_STATUS.ERROR);
      }
    }

    loadData();

    return () => {
      isMounted = false;
    };
  }, []);

  // The avatar uses the existing authoritative Settings/Profile read path on
  // an independent request. It never gates or sequences Program content; the
  // canonical placeholder remains a fully functional Settings trigger while
  // this request is unresolved (AV1-AV5).
  useEffect(() => {
    let isMounted = true;

    getUserSettings()
      .then((response) => {
        if (!isMounted) {
          return;
        }
        setProfileAvatar({
          url: response?.account?.profile?.avatar?.url || null,
          sex: response?.account?.profile?.sex || null,
        });
      })
      .catch(() => {
        // Avatar failure is deliberately non-blocking; the neutral placeholder
        // remains truthful and Program stays fully usable.
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const isWeeklyPlansResolved = weeklyPlansLoadStatus === LOAD_STATUS.RESOLVED;
  const isProgramOverviewResolved = programOverviewLoadStatus === LOAD_STATUS.RESOLVED;
  const featuredWeeklyPlan = useMemo(
    () => (isWeeklyPlansResolved ? weeklyPlans[0] || null : null),
    [isWeeklyPlansResolved, weeklyPlans]
  );
  // Bookmarked weekly plans win the preview outright: when at least one exists
  // the preview shows only bookmarks, and an unused second slot is deliberately
  // NOT backfilled with an unbookmarked plan. With no bookmarks the preview
  // falls back to the plain list. Both branches keep the backend list order
  // (published-precedence, createdAt descending) and cap at two rows. Bookmark
  // state is read-only here -- mutation stays on /program/all.
  const visibleWeeklyPlans = useMemo(() => {
    if (!isWeeklyPlansResolved) {
      return EMPTY_ARRAY;
    }

    const bookmarkedWeeklyPlans = weeklyPlans.filter((plan) => plan.isBookmarked);

    return bookmarkedWeeklyPlans.length > 0
      ? bookmarkedWeeklyPlans.slice(0, 2)
      : weeklyPlans.slice(0, 2);
  }, [isWeeklyPlansResolved, weeklyPlans]);
  const activeProgramCard = isProgramOverviewResolved
    ? programOverview?.activeProgramCard || null
    : null;
  const cycleStructure = isProgramOverviewResolved
    ? programOverview?.cycleStructure || null
    : null;
  const upcomingPrograms = isProgramOverviewResolved
    ? programOverview?.upcomingPrograms || EMPTY_ARRAY
    : EMPTY_ARRAY;
  const pastPrograms = isProgramOverviewResolved
    ? programOverview?.pastPrograms || EMPTY_ARRAY
    : EMPTY_ARRAY;

  const todayDateKey = useMemo(
    () => getLocalDateKey(programOverview?.timezone || "America/Toronto"),
    [programOverview?.timezone]
  );
  const hasActiveCycle = useMemo(
    () => isActiveOnDate(activeProgramCard, todayDateKey),
    [activeProgramCard, todayDateKey]
  );
  const hasUpcomingCycle = upcomingPrograms.length > 0;
  const programMode = hasActiveCycle ? "active" : hasUpcomingCycle ? "upcoming_only" : "empty";
  const programState = useMemo(() => {
    if (
      weeklyPlansLoadStatus === LOAD_STATUS.UNKNOWN ||
      programOverviewLoadStatus === LOAD_STATUS.UNKNOWN
    ) {
      return PROGRAM_STATE.UNKNOWN;
    }

    const hasResolvedContent = Boolean(
      activeProgramCard ||
        upcomingPrograms.length ||
        pastPrograms.length ||
        visibleWeeklyPlans.length
    );
    if (hasResolvedContent) {
      return PROGRAM_STATE.LOADED;
    }

    if (isWeeklyPlansResolved && isProgramOverviewResolved) {
      return PROGRAM_STATE.EMPTY;
    }

    return PROGRAM_STATE.ERROR;
  }, [
    activeProgramCard,
    isProgramOverviewResolved,
    isWeeklyPlansResolved,
    pastPrograms.length,
    programOverviewLoadStatus,
    upcomingPrograms.length,
    visibleWeeklyPlans.length,
    weeklyPlansLoadStatus,
  ]);
  const isCombinedEmptyState =
    programMode === "empty" && isWeeklyPlansResolved && visibleWeeklyPlans.length === 0;
  const displayedActiveCycle = useMemo(() => {
    const cycleCandidates = [activeProgramCard, ...upcomingPrograms, ...pastPrograms].filter(
      Boolean
    );

    return cycleCandidates.find((item) => isActiveOnDate(item, todayDateKey)) || activeProgramCard;
  }, [activeProgramCard, pastPrograms, todayDateKey, upcomingPrograms]);
  const timelineSlots = useMemo(() => {
    if (!isProgramOverviewResolved) {
      return EMPTY_ARRAY;
    }

    if (Array.isArray(cycleStructure?.slots) && cycleStructure.slots.length === 12) {
      return cycleStructure.slots;
    }

    return createNeutralTimelineSlots();
  }, [cycleStructure, isProgramOverviewResolved]);
  const timelineRows = useMemo(
    () => [timelineSlots.slice(0, 6), timelineSlots.slice(6, 12)],
    [timelineSlots]
  );
  const activeCycleRange = useMemo(() => {
    if (!activeProgramCard || Number(cycleStructure?.totalWeeks || 0) < 1) {
      return null;
    }

    return {
      startSlotIndex: 1,
      endSlotIndex: Number(cycleStructure.totalWeeks),
    };
  }, [activeProgramCard, cycleStructure]);
  const nextUpcomingProgram = upcomingPrograms[0] || null;
  const upcomingCycleRanges = useMemo(() => {
    if (programMode !== "upcoming_only") {
      return [];
    }

    const startOfCurrentWeek = getMondayWeekStart(todayDateKey);
    if (!startOfCurrentWeek) {
      return [];
    }

    const millisecondsPerWeek = 7 * 24 * 60 * 60 * 1000;

    return upcomingPrograms
      .map((program) => {
        const durationWeeks = Number(program?.durationWeeks || 0);
        const startOfCycleWeek = getMondayWeekStart(program?.startDate || null);

        if (!Number.isFinite(durationWeeks) || durationWeeks < 1 || !startOfCycleWeek) {
          return null;
        }

        const weeksUntilStart = Math.max(
          0,
          Math.round(
            (new Date(`${startOfCycleWeek}T00:00:00`) -
              new Date(`${startOfCurrentWeek}T00:00:00`)) /
              millisecondsPerWeek
          )
        );
        const startSlotIndex = 1 + weeksUntilStart;
        const endSlotIndex = startSlotIndex + durationWeeks - 1;

        if (endSlotIndex < 1 || startSlotIndex > 12) {
          return null;
        }

        return {
          cycleId: program.cycleId,
          startSlotIndex,
          endSlotIndex,
        };
      })
      .filter(Boolean);
  }, [programMode, todayDateKey, upcomingPrograms]);
  const firstUpcomingCycleRange = upcomingCycleRanges[0] || null;
  const nextCycleRange = useMemo(() => {
    if (programMode === "upcoming_only") {
      return firstUpcomingCycleRange;
    }

    if (!activeProgramCard) {
      return null;
    }

    const nextCycleStartSlot = timelineSlots.find(
      (slot) => slot.status === "next_cycle"
    )?.slotIndex;
    const nextCycleDuration = Number(upcomingPrograms[0]?.durationWeeks || 0);

    if (!Number.isFinite(nextCycleStartSlot) || nextCycleDuration < 1) {
      return null;
    }

    return {
      startSlotIndex: nextCycleStartSlot,
      endSlotIndex: nextCycleStartSlot + nextCycleDuration - 1,
    };
  }, [activeProgramCard, firstUpcomingCycleRange, programMode, timelineSlots, upcomingPrograms]);
  const activeProgramWeekLabel = useMemo(() => {
    if (!activeProgramCard) {
      return "";
    }

    const currentWeekNumber = Number(cycleStructure?.currentWeekNumber);
    const totalWeeks = Number(cycleStructure?.totalWeeks);

    if (!Number.isFinite(currentWeekNumber) || !Number.isFinite(totalWeeks) || totalWeeks < 1) {
      return "";
    }

    return `Week ${currentWeekNumber} of ${totalWeeks}`;
  }, [activeProgramCard, cycleStructure]);

  function closeSettings() {
    setIsSettingsOpen(false);
    settingsTriggerRef.current?.focus();
  }

  function navigateToWeeklyPlans() {
    navigate(getWeeklyPlansPath(), {
      state: {
        from: buildOrigin(location),
      },
    });
  }

  return (
    <DesignV2Scope className="lz-program" data-testid="program-root">
      <StickyPageHeader className="lz-program__header">
        <button
          ref={settingsTriggerRef}
          type="button"
          className="lz-program__avatar-trigger"
          aria-label="Open settings"
          onClick={() => setIsSettingsOpen(true)}
        >
          <Avatar
            src={profileAvatar.url}
            sex={profileAvatar.sex}
            size={40}
            data-testid="program-profile-avatar"
          />
        </button>

        <h1 className="lz-program__title">Program</h1>

        <Menu
          open={isCreateMenuOpen}
          onOpenChange={setIsCreateMenuOpen}
          trigger={
            <Button
              size="icon"
              className="lz-program__create-trigger"
              aria-label="Create program"
            >
              <PlusIcon />
            </Button>
          }
          menuClassName="lz-program__create-menu"
          aria-label="Create program options"
        >
          {isAIBuilderEnabled ? (
            <button
              type="button"
              role="menuitem"
              onClick={() =>
                navigate(getAIWeeklyPlanBuilderPath(), {
                  state: {
                    from: buildOrigin(location),
                  },
                })
              }
            >
              <SparkleIcon />
              AI builder
            </button>
          ) : null}
          <button
            type="button"
            role="menuitem"
            onClick={() =>
              navigate("/program/manual-new", {
                state: {
                  from: buildOrigin(location),
                  returnTo: "/program",
                },
              })
            }
          >
            <SlidersIcon />
            Manual
          </button>
        </Menu>
      </StickyPageHeader>

      <main className="lz-program__content" data-program-state={programState}>
        {programState === PROGRAM_STATE.UNKNOWN ? <ProgramLoading /> : null}

        {programOverviewLoadStatus === LOAD_STATUS.ERROR ? (
          <ProgramSectionError>
            Unable to load your program overview. Your weekly plans are still available below.
          </ProgramSectionError>
        ) : null}

        {isProgramOverviewResolved ? (
          <>
            <section className="lz-program__section" aria-label="Current Program">
              {programMode === "active" ? (
                <ProgramSummaryCard
                  title={
                    displayedActiveCycle?.name ||
                    featuredWeeklyPlan?.name ||
                    "No weekly plan yet"
                  }
                  status={activeProgramCard?.editorialStatus || featuredWeeklyPlan?.source || null}
                  detail={
                    activeProgramCard
                      ? `${activeProgramCard.referenceSessionsPerWeek} workouts / week`
                      : featuredWeeklyPlan
                        ? `${featuredWeeklyPlan.frequencyPerWeek} workouts / week`
                        : null
                  }
                  progressLabel={activeProgramWeekLabel}
                  progressPercent={Number(activeProgramCard?.dayProgressPercent || 0)}
                  dateRange={
                    displayedActiveCycle?.startDate && displayedActiveCycle?.endDate
                      ? `${formatDisplayDate(displayedActiveCycle.startDate)} - ${formatDisplayDate(
                          displayedActiveCycle.endDate
                        )}`
                      : activeProgramCard?.cycleDurationWeeks
                        ? `${activeProgramCard.cycleDurationWeeks} week cycle`
                        : null
                  }
                  actionLabel="View Details"
                  onAction={() => navigate(getCycleDetailsPath(activeProgramCard.cycleId))}
                />
              ) : programMode === "upcoming_only" ? (
                <ProgramSummaryCard
                  eyebrow="Upcoming Program"
                  title={nextUpcomingProgram?.name || "Upcoming cycle"}
                  status={nextUpcomingProgram?.editorialStatus || null}
                  detail="Your next cycle is already planned."
                  dateRange={
                    nextUpcomingProgram?.startDate
                      ? `Starts ${formatDisplayDate(nextUpcomingProgram.startDate)}`
                      : null
                  }
                  actionLabel="View Upcoming Cycle"
                  onAction={() => navigate(getCycleDetailsPath(nextUpcomingProgram.cycleId))}
                />
              ) : (
                <EmptyState
                  icon={<ProgramIcon />}
                  title="No active program"
                  body="Convert a weekly plan into a cycle to get started."
                  action={
                    <Button
                      onClick={() =>
                        isCombinedEmptyState
                          ? setIsCreateMenuOpen(true)
                          : navigateToWeeklyPlans()
                      }
                    >
                      {isCombinedEmptyState ? "Create a Weekly Plan" : "Browse Weekly Plans"}
                    </Button>
                  }
                />
              )}
            </section>

            <section className="lz-program__section" aria-labelledby="cycle-structure-heading">
              <h2 id="cycle-structure-heading" className="lz-program__section-title">
                Cycle Structure
              </h2>
              <ProgramCycleTimeline
                timelineRows={timelineRows}
                programMode={programMode}
                activeCycleRange={activeCycleRange}
                upcomingCycleRanges={upcomingCycleRanges}
                firstUpcomingCycleRange={firstUpcomingCycleRange}
                nextCycleRange={nextCycleRange}
              />
            </section>

            <section className="lz-program__section" aria-labelledby="upcoming-programs-heading">
              <div className="lz-program__section-heading">
                <h2 id="upcoming-programs-heading" className="lz-program__section-title">
                  Upcoming Programs
                </h2>
                {!(programMode === "empty" && upcomingPrograms.length === 0) ? (
                  <Button
                    variant="ghost"
                    className="lz-program__section-action lz-program__section-action--secondary"
                    onClick={() => navigate("/program/cycles")}
                  >
                    Browse
                  </Button>
                ) : null}
              </div>

              {upcomingPrograms.length ? (
                <Card padding="none" className="lz-program__list">
                  {upcomingPrograms.map((program) => (
                    <ListRow
                      key={program.cycleId}
                      className="lz-program__list-row lz-program__upcoming-row"
                      leading={<CalendarIcon />}
                      title={program.name}
                      description={`Starts ${formatDisplayDate(program.startDate) || "soon"}`}
                      meta={program.durationWeeks ? `${program.durationWeeks} week cycle` : null}
                      trailing={<ChevronRightIcon />}
                      onClick={() => navigate(getCycleDetailsPath(program.cycleId))}
                    />
                  ))}
                </Card>
              ) : (
                <EmptyState
                  className="lz-program__empty-list"
                  icon={<CalendarIcon />}
                  title="No upcoming programs yet"
                  body={
                    programMode === "empty"
                      ? isCombinedEmptyState
                        ? "First create a weekly plan and convert it into a cycle to see it here."
                        : "Convert a weekly plan into a cycle to see it here."
                      : "Future multi-week cycles will appear here."
                  }
                  action={
                    programMode === "empty" && !isCombinedEmptyState ? (
                      <Button variant="secondary" onClick={navigateToWeeklyPlans}>
                        Browse Plans
                      </Button>
                    ) : null
                  }
                  titleAs="h3"
                />
              )}
            </section>

            <section className="lz-program__section" aria-labelledby="past-programs-heading">
              <div className="lz-program__section-heading">
                <h2 id="past-programs-heading" className="lz-program__section-title">
                  Past Programs
                </h2>
                {pastPrograms.length > 0 ? (
                  <Button variant="ghost" className="lz-program__section-action" onClick={() => navigate("/program/cycles")}>
                    Browse
                  </Button>
                ) : null}
              </div>

              {pastPrograms.length ? (
                <Card padding="none" className="lz-program__list">
                  {pastPrograms.map((program) => (
                    <ListRow
                      key={program.cycleId}
                      className="lz-program__list-row"
                      leading={<HistoryIcon />}
                      title={program.name}
                      description={`Completed ${formatDisplayDate(program.endDate) || "previously"}`}
                      meta={program.durationWeeks ? `${program.durationWeeks} week cycle` : null}
                      trailing={<ChevronRightIcon />}
                      onClick={() => navigate(getCycleDetailsPath(program.cycleId))}
                    />
                  ))}
                </Card>
              ) : (
                <EmptyState
                  className="lz-program__empty-list"
                  icon={<HistoryIcon />}
                  title="No past programs yet"
                  body="Completed multi-week cycles will appear here."
                  titleAs="h3"
                />
              )}
            </section>
          </>
        ) : null}

        {weeklyPlansLoadStatus === LOAD_STATUS.ERROR ? (
          <section className="lz-program__section" aria-labelledby="all-programs-heading">
            <h2 id="all-programs-heading" className="lz-program__section-title">
              All Programs
            </h2>
            <ProgramSectionError>
              Unable to load weekly plans. Your cycle overview remains available above.
            </ProgramSectionError>
          </section>
        ) : null}

        {isWeeklyPlansResolved ? (
          <section className="lz-program__section" aria-labelledby="all-programs-heading">
            <div className="lz-program__section-heading">
              <h2 id="all-programs-heading" className="lz-program__section-title">
                All Programs
              </h2>
              {!isCombinedEmptyState ? (
                <Button variant="ghost" className="lz-program__section-action" onClick={navigateToWeeklyPlans}>
                  See All
                </Button>
              ) : null}
            </div>

            {visibleWeeklyPlans.length ? (
              <Card padding="none" className="lz-program__list">
                {visibleWeeklyPlans.map((plan) => (
                  <ListRow
                    key={plan.id}
                    className="lz-program__list-row lz-program__weekly-plan-row"
                    data-bookmarked={plan.isBookmarked ? "true" : "false"}
                    title={plan.name}
                    description={`${plan.frequencyPerWeek} workouts / ${plan.totalWeeklySets} total sets`}
                    meta={<WeeklyPlanMetadata source={plan.source} status={plan.status} />}
                    trailing={<BookmarkIcon filled={plan.isBookmarked} />}
                    onClick={() =>
                      navigate(getWeeklyPlanDetailsPath(plan.weeklyPlanParentId), {
                        state: {
                          from: buildOrigin(location),
                        },
                      })
                    }
                  />
                ))}
              </Card>
            ) : (
              <EmptyState
                className="lz-program__empty-list"
                icon={<ProgramIcon />}
                title="No weekly programs yet"
                body="Your workout programs will appear here once you create one."
                titleAs="h3"
              />
            )}
          </section>
        ) : null}
      </main>

      <SettingsDrawer isOpen={isSettingsOpen} onClose={closeSettings} />
    </DesignV2Scope>
  );
}
